import { useState, type ReactNode } from 'react';
import '@/styles/webreg/index.css';
import { HappyHeartsFooter } from '@/components/HappyHeartsFooter';
import { WregAside } from './WregAside';
import { WregHelp } from './WregHelp';
import { WregTopbar } from './WregTopbar';
import { useWregDesktop } from './useWregDesktop';
import { EVENT_SOURCES, type EventSource } from '@/consts/events';
import { WREG_STEP_NAME, type WregStep } from './wregSteps';

type Props = {
  step: WregStep;
  canBack: boolean;
  onBack: () => void;
  children: ReactNode;
  helpOpen?: boolean;
  onHelpOpenChange?: (open: boolean, source?: EventSource) => void;
  helpSource?: EventSource;
  onHome?: () => void;
};

const WregShell = ({
  step,
  canBack,
  onBack,
  children,
  helpOpen,
  onHelpOpenChange,
  helpSource,
  onHome,
}: Props) => {
  const isDesktop = useWregDesktop();
  const [ownHelpOpen, setOwnHelpOpen] = useState(false);
  const open = helpOpen ?? ownHelpOpen;
  const showBack = canBack && step !== 'allset';
  const setOpen = (next: boolean, source?: EventSource) => {
    setOwnHelpOpen(next);
    onHelpOpenChange?.(next, source);
  };
  const help = (
    <WregHelp
      open={open}
      onClose={() => setOpen(false)}
      stepName={WREG_STEP_NAME[step]}
      source={helpSource ?? EVENT_SOURCES.TOP_BAR}
    />
  );
  const body = (
    <div className="rg-surface" data-wreg-step={step}>
      {children}
    </div>
  );

  if (isDesktop) {
    return (
      <div className="reg-site-shell">
        <div className="reg-bw-body">
          <WregTopbar
            variant="desktop"
            canBack={showBack}
            onBack={onBack}
            onHelp={() => setOpen(true, EVENT_SOURCES.TOP_BAR)}
            onHome={onHome}
          />
          <div className="reg-dt-cols">
            <WregAside step={step} />
            <div className="reg-dt-main">{body}</div>
          </div>
          {help}
        </div>
        <HappyHeartsFooter />
      </div>
    );
  }

  return (
    <div className="reg-site-shell">
      <div className="reg-phone-screen">
        <WregTopbar
          variant="mobile"
          canBack={showBack}
          onBack={onBack}
          onHelp={() => setOpen(true, EVENT_SOURCES.TOP_BAR)}
          onHome={onHome}
        />
        <div className="reg-mob-content">{body}</div>
        {help}
      </div>
      <HappyHeartsFooter />
    </div>
  );
};

export { WregShell };
